"use client";

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { Icon } from "../../components/Icon";
import { Faq } from "../../components/Faq";
import { JsonLd } from "../../components/JsonLd";
import { softwareAppSchema } from "@/lib/schema";
import { getAiWorkflowsData } from "./ai_workflows_data";

function AnimatedCounter({ value, color }: { value: string; color: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: false });
  const [displayed, setDisplayed] = useState("0");

  useEffect(() => {
    if (!isInView) return;
    const match = value.match(/^([<+↓]?)(\d+\.?\d*)(%|×|s|min)?$/);
    if (!match) { setDisplayed(value); return; }
    const prefix = match[1] || "";
    const target = parseFloat(match[2]);
    const suffix = match[3] || "";
    const duration = 1200;
    const step = 16;
    const steps = duration / step;
    const increment = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        clearInterval(timer);
        setDisplayed(`${prefix}${target}${suffix}`);
      } else {
        const display = Number.isInteger(target) ? Math.floor(current) : current.toFixed(1);
        setDisplayed(`${prefix}${display}${suffix}`);
      }
    }, step);
    return () => clearInterval(timer);
  }, [isInView, value]);

  return (
    <span ref={ref} style={{ color, fontVariantNumeric: "tabular-nums" }}>
      {displayed}
    </span>
  );
}

export default function AiWorkflowsClient({ locale }: { locale: string }) {
  const t = getAiWorkflowsData(locale);
  const [current, setCurrent] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { once: false, amount: 0.3 });
  const stepperRef = useRef<HTMLDivElement>(null);

  const progressBarRef = useRef<HTMLDivElement>(null);
  const elapsedRef = useRef(0);
  const lastCurrentRef = useRef(current);
  const DURATION = 6000;

  // Reset elapsed timer when slide changes (whether auto or manual)
  useEffect(() => {
    if (current !== lastCurrentRef.current) {
      elapsedRef.current = 0;
      lastCurrentRef.current = current;
      if (progressBarRef.current) {
        progressBarRef.current.style.width = "0%";
      }
    }
  }, [current]);

  // Auto-scroll the horizontal active stepper tab into the center of the viewport on mobile
  useEffect(() => {
    if (stepperRef.current) {
      const activeEl = stepperRef.current.querySelector(".aiwf-step.active") as HTMLElement;
      if (activeEl) {
        activeEl.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [current]);

  // Frame-accurate progress animation & auto-advance loop
  useEffect(() => {
    if (!autoPlay || !isInView || isHovered) return;

    let lastTime = performance.now();
    let frameId: number;

    const tick = (now: number) => {
      const delta = now - lastTime;
      lastTime = now;

      elapsedRef.current += delta;

      if (elapsedRef.current >= DURATION) {
        elapsedRef.current = 0;
        setCurrent((c) => (c + 1) % t.cases.length);
        lastCurrentRef.current = (current + 1) % t.cases.length;
        if (progressBarRef.current) {
          progressBarRef.current.style.width = "0%";
        }
        return;
      }

      if (progressBarRef.current) {
        const pct = Math.min((elapsedRef.current / DURATION) * 100, 100);
        progressBarRef.current.style.width = `${pct}%`;
      }

      frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [autoPlay, isInView, isHovered, current, t.cases.length]);

  const uc = t.cases[current];
  const problem = uc.rows[0];
  const aiInChikitsa = uc.rows[1];
  const impact = uc.rows[2];
  const moduleRow = uc.rows[3];
  const aiCapability = uc.rows[4];

  return (
    <>
      <style>{`
        .aiwf-showcase {
          padding: 60px 0 20px;
          background: var(--bg);
          position: relative;
          overflow: hidden;
        }
        .aiwf-layout {
          display: grid;
          grid-template-columns: 300px 1fr;
          gap: 40px;
          align-items: stretch;
          max-width: 1240px;
          margin: 0 auto;
        }

        /* ─── LEFT STEPPER ─── */
        .aiwf-stepper {
          display: flex;
          flex-direction: column;
          height: 100%;
          padding-right: 24px;
          border-right: 1px solid rgba(11,23,51,0.06);
          box-sizing: border-box;
        }
        .aiwf-stepper-label {
          font-family: var(--font-mono);
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: rgba(11,23,51,0.35);
          margin-bottom: 12px;
          padding-left: 14px;
          flex-shrink: 0;
        }
        .aiwf-step {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 8px 14px;
          cursor: pointer;
          border: none;
          background: transparent;
          text-align: left;
          width: 100%;
          position: relative;
          border-radius: 12px;
          transition: all 0.22s cubic-bezier(0.4, 0, 0.2, 1);
          border-left: 3px solid transparent;
          flex: 1;
          min-height: 38px;
        }
        .aiwf-step:hover:not(.active) {
          background: rgba(11,23,51,0.015);
        }
        .aiwf-step.active {
          background: var(--step-accent);
          border-left-color: var(--step-color);
        }
        .aiwf-step-num {
          font-family: var(--font-mono);
          font-size: 10px;
          font-weight: 700;
          color: rgba(11,23,51,0.4);
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: rgba(11,23,51,0.04);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: all 0.22s ease;
        }
        .aiwf-step.active .aiwf-step-num {
          background: var(--step-color);
          color: #fff;
        }
        .aiwf-step-text {
          font-size: 13px;
          font-weight: 500;
          color: rgba(11,23,51,0.45);
          line-height: 1.35;
          transition: color 0.22s ease;
          white-space: normal;
        }
        .aiwf-step.active .aiwf-step-text {
          color: var(--ink);
          font-weight: 600;
        }

        /* ─── RIGHT CONTENT CARD ─── */
        .aiwf-card-wrap {
          min-width: 0;
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        .aiwf-card {
          position: relative;
          border-radius: 24px;
          border: 1px solid rgba(11,23,51,0.07);
          background: #fff;
          box-shadow: 
            0 2px 1px rgba(11,23,51,0.01), 
            0 8px 24px rgba(11,23,51,0.03), 
            0 32px 64px rgba(11,23,51,0.04);
          overflow: hidden;
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 500px;
        }
        .aiwf-progress-bar {
          position: absolute;
          top: 0;
          left: 0;
          height: 4px;
          background: var(--progress-color);
          width: 0%;
          z-index: 10;
          border-top-left-radius: 24px;
          border-top-right-radius: 24px;
        }
        .aiwf-hero {
          padding: 32px 36px 24px;
          position: relative;
          overflow: hidden;
          background: #ffffff;
          border-bottom: 1px solid rgba(11,23,51,0.06);
        }
        /* Geometric blueprints background dot grid */
        .aiwf-hero::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image: radial-gradient(rgba(11,23,51,0.04) 1px, transparent 1px);
          background-size: 16px 16px;
          pointer-events: none;
        }
        /* Splash shape effect in the top right corner */
        .aiwf-hero-splash {
          position: absolute;
          top: -90px;
          right: -90px;
          width: 320px;
          height: 320px;
          pointer-events: none;
          z-index: 0;
          transition: color 0.4s ease;
        }
        .aiwf-hero-splash svg {
          width: 100%;
          height: 100%;
        }
        .aiwf-hero-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 20px;
          position: relative;
          z-index: 1;
        }
        .aiwf-hero-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .aiwf-icon-pill {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-weight: 600;
          box-shadow: 0 4px 10px rgba(11,23,51,0.02);
        }
        .aiwf-num-label {
          font-family: var(--font-mono);
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 0.12em;
          color: var(--ink-4);
          text-transform: uppercase;
        }
        .aiwf-module-tag {
          font-size: 11px;
          font-weight: 700;
          padding: 6px 14px;
          border-radius: 99px;
          letter-spacing: 0.04em;
        }
        .aiwf-card-h2 {
          font-size: clamp(20px, 2.8vw, 26px);
          font-weight: 500;
          color: var(--ink);
          letter-spacing: -0.02em;
          line-height: 1.25;
          margin: 0 0 10px;
          position: relative;
          z-index: 1;
        }
        .aiwf-card-p {
          font-size: 14.5px;
          color: var(--ink-3);
          line-height: 1.55;
          margin: 0;
          max-width: 620px;
          position: relative;
          z-index: 1;
        }

        /* ─── Metrics Section ─── */
        .aiwf-metrics {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0;
          border-bottom: 1px solid rgba(11,23,51,0.06);
          background: rgba(11,23,51,0.005);
        }
        .aiwf-metric {
          padding: 18px 24px;
          border-right: 1px solid rgba(11,23,51,0.06);
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 4px;
        }
        .aiwf-metric:last-child {
          border-right: none;
        }
        .aiwf-mval {
          font-size: 28px;
          font-weight: 700;
          letter-spacing: -0.03em;
          line-height: 1.1;
        }
        .aiwf-mval--sm {
          font-size: 13.5px !important;
          font-weight: 600 !important;
          letter-spacing: 0 !important;
          line-height: 1.35 !important;
          white-space: normal;
          display: block;
        }
        .aiwf-mlbl {
          font-size: 11px;
          color: rgba(11,23,51,0.40);
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        /* ─── Triptych Section ─── */
        .aiwf-triptych {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          flex: 1;
        }
        .aiwf-triptych-cell {
          padding: 24px 28px;
          border-right: 1px solid rgba(11,23,51,0.06);
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .aiwf-triptych-cell:last-child {
          border-right: none;
        }
        .aiwf-cell-head {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .aiwf-cell-label {
          font-family: var(--font-mono);
          font-size: 9.5px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }
        .aiwf-cell-label--accent {
          color: var(--cell-accent, #0A9E9A);
        }
        .aiwf-cell-body {
          font-size: 13.5px;
          line-height: 1.5;
          color: var(--ink-2);
          margin: 0;
        }
        .aiwf-cell-body--impact {
          font-weight: 600;
          color: var(--ink);
        }

        /* ─── Footer Section ─── */
        .aiwf-foot {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 28px;
          background: rgba(11,23,51,0.01);
          border-top: 1px solid rgba(11,23,51,0.05);
          flex-shrink: 0;
        }
        .aiwf-foot-meta {
          display: flex;
          gap: 24px;
        }
        .aiwf-foot-kv {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .aiwf-foot-k {
          font-family: var(--font-mono);
          font-size: 8.5px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: rgba(11,23,51,0.3);
        }
        .aiwf-foot-v {
          font-size: 11.5px;
          font-weight: 600;
          color: var(--ink-3);
        }
        .aiwf-pips {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .aiwf-pip {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: rgba(11,23,51,0.12);
          border: none;
          cursor: pointer;
          padding: 0;
          transition: all 0.25s ease;
          flex-shrink: 0;
        }
        .aiwf-pip.active {
          width: 18px;
          border-radius: 3px;
        }

        /* ─── Mobile / Responsiveness ─── */
        .aiwf-mobile-pips {
          display: flex;
          justify-content: center;
          gap: 6px;
          margin-top: 16px;
        }
        @media (max-width: 960px) {
          .aiwf-layout {
            grid-template-columns: 1fr;
            gap: 24px;
          }
          /* Hide duplicate navigation dots in footer on mobile */
          .aiwf-pips {
            display: none !important;
          }
          /* Convert vertical stepper to horizontal scrollable tab strip */
          .aiwf-stepper {
            display: flex !important;
            flex-direction: row !important;
            overflow-x: auto !important;
            width: 100% !important;
            padding: 4px 6px !important;
            border-right: none !important;
            border-bottom: 1px solid rgba(11,23,51,0.08) !important;
            gap: 12px !important;
            scrollbar-width: none !important; /* Firefox */
            margin-bottom: 16px !important;
            -webkit-overflow-scrolling: touch;
          }
          .aiwf-stepper::-webkit-scrollbar {
            display: none !important; /* Safari & Chrome */
          }
          .aiwf-stepper-label {
            display: none !important;
          }
          .aiwf-step {
            flex: 0 0 auto !important;
            width: auto !important;
            white-space: nowrap !important;
            padding: 10px 16px !important;
            border-left: none !important;
            border-bottom: 3px solid transparent !important;
            border-radius: 0 !important;
            min-height: unset !important;
          }
          .aiwf-step.active {
            background: transparent !important;
            border-bottom-color: var(--step-color) !important;
          }
          .aiwf-step-num {
            width: 18px !important;
            height: 18px !important;
            font-size: 8px !important;
          }
          .aiwf-step-text {
            font-size: 12.5px !important;
          }

          /* Tablet layouts preserve 3 columns for metrics & triptych cards */
          .aiwf-metrics {
            grid-template-columns: repeat(3, 1fr) !important;
          }
          .aiwf-metric {
            border-bottom: none !important;
            border-right: 1px solid rgba(11,23,51,0.06) !important;
            padding: 16px 20px !important;
          }
          .aiwf-metric:last-child {
            border-right: none !important;
          }
          .aiwf-triptych {
            grid-template-columns: repeat(3, 1fr) !important;
          }
          .aiwf-triptych-cell {
            border-bottom: none !important;
            border-right: 1px solid rgba(11,23,51,0.06) !important;
            padding: 20px 20px !important;
          }
          .aiwf-triptych-cell:last-child {
            border-right: none !important;
          }
          .aiwf-hero {
            padding: 24px 24px 18px !important;
          }
          .aiwf-card {
            min-height: auto !important;
          }
          .aiwf-hero-splash {
            width: 280px !important;
            height: 280px !important;
            top: -80px !important;
            right: -80px !important;
          }
        }

        @media (max-width: 580px) {
          .aiwf-metrics {
            grid-template-columns: 1fr !important;
          }
          .aiwf-metric {
            border-right: none !important;
            border-bottom: 1px solid rgba(11,23,51,0.06) !important;
            padding: 14px 16px !important;
          }
          .aiwf-metric:last-child {
            border-bottom: none !important;
          }
          .aiwf-triptych {
            grid-template-columns: 1fr !important;
          }
          .aiwf-triptych-cell {
            border-right: none !important;
            border-bottom: 1px solid rgba(11,23,51,0.06) !important;
            padding: 18px 16px !important;
          }
          .aiwf-triptych-cell:last-child {
            border-bottom: none !important;
          }
        }

        @media (max-width: 480px) {
          .aiwf-card {
            border-radius: 18px !important;
          }
          .aiwf-progress-bar {
            border-top-left-radius: 18px !important;
            border-top-right-radius: 18px !important;
          }
          .aiwf-hero {
            padding: 18px 16px 14px !important;
          }
          .aiwf-hero-splash {
            width: 210px !important;
            height: 210px !important;
            top: -55px !important;
            right: -55px !important;
          }
          .aiwf-card-h2 {
            font-size: 19px !important;
            margin-bottom: 8px !important;
          }
          .aiwf-card-p {
            font-size: 13.5px !important;
            line-height: 1.45 !important;
          }
          .aiwf-mval {
            font-size: 23px !important;
          }
          .aiwf-mlbl {
            font-size: 9.5px !important;
          }
          .aiwf-cell-body {
            font-size: 12.8px !important;
            line-height: 1.45 !important;
          }
          .aiwf-foot {
            padding: 12px 16px !important;
            flex-direction: column !important;
            align-items: center !important;
            gap: 12px !important;
            text-align: center !important;
          }
          .aiwf-foot-meta {
            gap: 16px !important;
          }
          .aiwf-foot-kv {
            align-items: center !important;
          }
        }

        @media (min-width: 961px) {
          .aiwf-mobile-pips {
            display: none;
          }
        }
        .faq-section {
          padding-top: 40px !important;
        }
      `}</style>

      <section className="aiwf-showcase">
        <div className="container">
          <div className="aiwf-layout" ref={containerRef}>

            <nav ref={stepperRef} className="aiwf-stepper" aria-label="Use case navigation">
              <span className="aiwf-stepper-label">{t.stepperLabel}</span>
              {t.cases.map((c, i) => (
                <button
                  key={c.num}
                  className={`aiwf-step${i === current ? " active" : ""}`}
                  style={{ 
                    ["--step-color" as any]: c.color,
                    ["--step-accent" as any]: c.accent
                  }}
                  onClick={() => { setCurrent(i); setAutoPlay(false); }}
                  aria-current={i === current ? "true" : undefined}
                >
                  <span className="aiwf-step-num">{c.numShort}</span>
                  <span className="aiwf-step-text">{c.title}</span>
                </button>
              ))}
            </nav>

            <div
              className="aiwf-card-wrap"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={current}
                  initial={{ opacity: 0, y: 12, scale: 0.99 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -12, scale: 0.99 }}
                  transition={{ duration: 0.48, ease: [0.16, 1, 0.3, 1] }}
                  className="aiwf-card"
                >
                  {autoPlay && (
                    <div 
                      ref={progressBarRef}
                      className="aiwf-progress-bar" 
                      style={{
                        ["--progress-color" as any]: uc.color,
                      }}
                    />
                  )}
                  <div className="aiwf-hero">
                    {/* Solid abstract shape corner splash effect */}
                    <div className="aiwf-hero-splash" style={{ color: uc.color }}>
                      <svg viewBox="0 0 100 100" fill="currentColor" aria-hidden="true">
                        <path d="M30 0 C70 0, 100 30, 100 70 C100 85, 85 100, 70 100 C30 100, 0 70, 0 30 C0 15, 15 0, 30 0 Z" opacity="0.08" />
                        <path d="M40 10 C75 10, 95 30, 95 60 C95 72, 82 85, 70 85 C40 85, 15 65, 15 35 C15 22, 25 10, 40 10 Z" opacity="0.05" />
                      </svg>
                    </div>
                    
                    <div className="aiwf-hero-top">
                      <div className="aiwf-hero-left">
                        <motion.div
                          initial={{ scale: 0.6, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ delay: 0.04, type: "spring", stiffness: 380, damping: 26 }}
                          className="aiwf-icon-pill"
                          style={{ background: uc.accent, color: uc.color }}
                        >
                          <Icon name={uc.icon} width={18} height={18} />
                        </motion.div>
                        <span className="aiwf-num-label">{uc.num}</span>
                      </div>
                      <motion.span
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.08 }}
                        className="aiwf-module-tag"
                        style={{ background: uc.accent, color: uc.color }}
                      >
                        {uc.tag}
                      </motion.span>
                    </div>

                    <motion.h2
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.07 }}
                      className="aiwf-card-h2"
                    >
                      {uc.title}
                    </motion.h2>
                    <motion.p
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.11 }}
                      className="aiwf-card-p"
                    >
                      {uc.intro}
                    </motion.p>
                  </div>

                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.14 }}
                    className="aiwf-metrics"
                  >
                    <div className="aiwf-metric">
                      <span className="aiwf-mval">
                        <AnimatedCounter value={uc.metricVal} color={uc.color} />
                      </span>
                      <span className="aiwf-mlbl">{uc.metricLabel}</span>
                    </div>
                    <div className="aiwf-metric">
                      <span className={`aiwf-mval aiwf-mval--sm`} style={{ color: uc.color }}>
                        {moduleRow?.dd}
                      </span>
                      <span className="aiwf-mlbl">{t.moduleLabel}</span>
                    </div>
                    <div className="aiwf-metric">
                      <span className={`aiwf-mval aiwf-mval--sm`} style={{ color: uc.color }}>
                        {aiCapability?.dd}
                      </span>
                      <span className="aiwf-mlbl">{t.aiEngineLabel}</span>
                    </div>
                  </motion.div>

                  <div className="aiwf-triptych">
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.17 }}
                      className="aiwf-triptych-cell"
                    >
                      <div className="aiwf-cell-head">
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                          <circle cx="8" cy="8" r="7" stroke={uc.color} strokeWidth="1.5"/>
                          <path d="M8 5v3.5" stroke={uc.color} strokeWidth="1.5" strokeLinecap="round"/>
                          <circle cx="8" cy="11.2" r="0.8" fill={uc.color}/>
                        </svg>
                        <span className="aiwf-cell-label" style={{ color: uc.color }}>{problem?.dt}</span>
                      </div>
                      <p className="aiwf-cell-body">{problem?.dd}</p>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.21 }}
                      className="aiwf-triptych-cell"
                    >
                      <div className="aiwf-cell-head">
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                          <path d="M8 2l1.4 3.3L13 7l-3.6 1.7L8 12l-1.4-3.3L3 7l3.6-1.7z" stroke={uc.color} strokeWidth="1.4" strokeLinejoin="round"/>
                        </svg>
                        <span className="aiwf-cell-label" style={{ color: uc.color }}>{aiInChikitsa?.dt}</span>
                      </div>
                      <p className="aiwf-cell-body">{aiInChikitsa?.dd}</p>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.25 }}
                      className="aiwf-triptych-cell"
                      style={{
                        background: uc.accent,
                        ["--cell-accent" as any]: uc.color,
                      }}
                    >
                      <div className="aiwf-cell-head">
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                          <path d="M2 12l4-5 3 3 5-7" stroke={uc.color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span className="aiwf-cell-label aiwf-cell-label--accent">{impact?.dt}</span>
                      </div>
                      <p className="aiwf-cell-body aiwf-cell-body--impact">{impact?.dd}</p>
                    </motion.div>
                  </div>

                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.28 }}
                    className="aiwf-foot"
                  >
                    <div className="aiwf-foot-meta">
                      <div className="aiwf-foot-kv">
                        <span className="aiwf-foot-k">{t.moduleLabel}</span>
                        <span className="aiwf-foot-v">{moduleRow?.dd}</span>
                      </div>
                      <div className="aiwf-foot-kv">
                        <span className="aiwf-foot-k">{t.aiEngineLabel}</span>
                        <span className="aiwf-foot-v" style={{ color: uc.color }}>{aiCapability?.dd}</span>
                      </div>
                    </div>
                    <div className="aiwf-pips">
                      {t.cases.map((_, i) => (
                        <button
                          key={i}
                          className={`aiwf-pip${i === current ? " active" : ""}`}
                          style={i === current ? { background: uc.color } : {}}
                          onClick={() => { setCurrent(i); setAutoPlay(false); }}
                          aria-label={`Go to use case ${i + 1}`}
                        />
                      ))}
                    </div>
                  </motion.div>
                </motion.div>
              </AnimatePresence>

              <div className="aiwf-mobile-pips">
                {t.cases.map((_, i) => (
                  <button
                    key={i}
                    className={`aiwf-pip${i === current ? " active" : ""}`}
                    style={i === current ? { background: uc.color } : {}}
                    onClick={() => { setCurrent(i); setAutoPlay(false); }}
                    aria-label={`Go to use case ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Faq
        items={t.faqs}
        eyebrow={t.faqsEyebrow}
        heading={t.faqsTitle}
        intro={t.faqsDesc}
      />

      <JsonLd
        data={softwareAppSchema({
          name: "Chikitsa AI Workflows",
          description:
            "AI hospital workflow automation across appointment booking, OPD documentation, prescription digitisation, NHCX claims, discharge summaries, MIS analytics and patient engagement.",
          path: "/ai-workflows",
        })}
      />
    </>
  );
}
